import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { ArrowLeft, Calendar } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import StarRating from '../components/common/StarRating';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const CATEGORY_DETAIL_LABELS = {
  attendance: [
    ['presentDays', 'Present Days'],
    ['absentDays', 'Absent Days'],
    ['leaveDays', 'Leave Days'],
    ['weekOffs', 'Week Offs'],
    ['holidays', 'Holidays'],
  ],
  punctuality: [
    ['lateIn', 'Late Time In'],
    ['earlyOut', 'Early Time Out'],
    ['presentDays', 'Present Days'],
  ],
  checklist: [
    ['done', 'Tasks Completed'],
    ['total', 'Total Tasks'],
    ['days', 'Days Counted'],
  ],
  discipline: [
    ['fineCount', 'Fines'],
    ['fineAmount', 'Fine Amount (₹)'],
    ['shortLeaveCount', 'Short Leaves'],
    ['shortLeaveHours', 'Short Leave Hours'],
  ],
  overtime: [
    ['overtimeHours', 'Total Overtime (hrs)'],
    ['avgPerDay', 'Avg / Day (hrs)'],
  ],
};

export default function PerformanceDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const [staff, setStaff] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [perf, setPerf] = useState(null);

  const monthStart = startOfMonth(new Date());
  const monthEnd = endOfMonth(new Date());
  const [range, setRange] = useState([monthStart, monthEnd]);
  const [rangeStart, rangeEnd] = range;
  const [appliedFrom, setAppliedFrom] = useState(format(monthStart, 'yyyy-MM-dd'));
  const [appliedTo, setAppliedTo] = useState(format(monthEnd, 'yyyy-MM-dd'));
  const dpRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get(`/staff/${staffId}`)
      .then((r) => { if (active) setStaff(r.data); })
      .catch((err) => {
        if (!active) return;
        const msg = err.response?.data?.message || err.message || 'Unable to load staff';
        const status = err.response?.status;
        setAccessError(status === 403 ? 'Access denied — you do not have permission to view this staff member.' : msg);
        setNotFound(true);
      });
    return () => { active = false; };
  }, [staffId]);

  const applyRange = () => {
    setAppliedFrom(rangeStart ? format(rangeStart, 'yyyy-MM-dd') : '');
    setAppliedTo(rangeEnd ? format(rangeEnd, 'yyyy-MM-dd') : '');
    dpRef.current?.setOpen(false);
  };

  const fetchPerformance = useCallback(async () => {
    if (!staffId || !appliedFrom || !appliedTo) return;
    const res = await api.get(`/performance/${staffId}?from=${appliedFrom}&to=${appliedTo}`);
    setPerf(res.data);
  }, [staffId, appliedFrom, appliedTo]);

  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  if (notFound) {
    return (
      <div className="page">
        <div className="empty-state">
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Could not load staff member</div>
          {accessError && (
            <div style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, maxWidth: 420 }}>{accessError}</div>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/performance')}>Back to Performance</button>
        </div>
      </div>
    );
  }

  if (!staff) {
    return <div className="page"><div className="loading-screen">Loading...</div></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <button className="back-arrow-btn" onClick={() => navigate('/performance')} title="Back">
            <ArrowLeft size={22} />
          </button>
          {staff.photo && <img src={`${API_BASE}/uploads/${staff.photo}`} alt="" className="staff-photo-sm" />}
          <div>
            <h2 style={{ fontSize: 20 }}>{staff.name}</h2>
            <p className="text-muted">{staff.staffId} · {staff.outlet?.name}</p>
          </div>
        </div>
        <div className="header-actions">
          <DatePicker
            ref={dpRef}
            showIcon
            icon={<Calendar size={16} />}
            selectsRange
            startDate={rangeStart}
            endDate={rangeEnd}
            onChange={(update) => {
              const u = update || [null, null];
              setRange(u);
              if (!u[0] && !u[1]) { setAppliedFrom(''); setAppliedTo(''); }
            }}
            shouldCloseOnSelect={false}
            isClearable
            dateFormat="dd MMM yyyy"
            placeholderText="Select date range"
            className="form-input datepicker-input"
            popperPlacement="bottom-end"
            popperProps={{ strategy: 'fixed' }}
          >
            <div className="datepicker-footer">
              <button type="button" className="btn btn-sm btn-primary" onClick={applyRange}>OK</button>
            </div>
          </DatePicker>
        </div>
      </div>

      {perf && (
        <>
          <div className="perf-overall">
            <div>
              <p className="text-muted">Overall Rating</p>
              <h3 style={{ margin: '4px 0' }}>{perf.overallRating != null ? perf.overallRating.toFixed(1) : '—'} / 5</h3>
              <p className="text-muted">Period: {perf.period.from} to {perf.period.to}</p>
            </div>
            <StarRating value={perf.overallRating} size={28} />
          </div>

          <div className="performance-grid">
            {perf.categories.map((c) => (
              <div key={c.key} className="perf-card">
                <div className="perf-card-header">
                  <h4>{c.label}</h4>
                  <StarRating value={c.rating} />
                </div>
                <p className="perf-card-pct">{c.pct != null ? `${c.pct}%` : 'No data'}</p>
                <div className="perf-card-details">
                  {(CATEGORY_DETAIL_LABELS[c.key] || []).map(([key, label]) => (
                    <div key={key} className="perf-detail-row">
                      <span>{label}</span>
                      <span>{c.detail[key]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
